import { useMemo, useState } from 'react';
import { AlertTriangle, History, Plus, Save, Trash2 } from 'lucide-react';
import { ErreurApi } from '../../lib/api';
import {
  jourFormulaire,
  jourReunion,
  montantEuros,
  useEnregistrerBareme,
  useEnregistrerValeurReference,
  useParametresRemuneration,
  type BaremeRemuneration,
  type LigneBareme,
} from '../../lib/useParametresRemuneration';
import { useToast } from '../ui/useToast';
import { BoutonMint, BoutonSecondaire, MessageErreur, Pastille } from '../ui/Primitives';

/**
 * Réglage de la rémunération : valeur de référence et barèmes.
 *
 * Ce que cet écran rend possible sans déploiement : revaloriser le SMIC,
 * corriger un taux, ajouter une tranche d'âge ou une année. Aucun pourcentage
 * ni montant n'est écrit dans le front — tout vient de l'API, qui les lit en
 * base, et les montants affichés sont ceux qu'elle calcule.
 *
 * Deux règles de conservation s'y lisent, parce qu'elles surprennent sinon :
 * une valeur nouvelle n'écrase pas l'ancienne, elle porte sa date d'effet ; et
 * un barème qui a déjà servi à calculer des contrats ne se corrige plus — il se
 * remplace par un barème daté du jour où la règle change.
 */

/** Une ligne en cours d'édition, avant envoi. */
type LigneBrouillon = Omit<LigneBareme, 'id' | 'montant'>;

function enBrouillon(bareme: BaremeRemuneration): LigneBrouillon[] {
  return bareme.lignes.map((ligne) => ({
    libelleTranche: ligne.libelleTranche,
    ageMin: ligne.ageMin,
    ageMax: ligne.ageMax,
    rangAnnee: ligne.rangAnnee,
    critere: ligne.critere,
    pourcentage: ligne.pourcentage,
    base: ligne.base,
    ordre: ligne.ordre,
  }));
}

/**
 * Montant mensuel d'un taux, calculé comme le serveur le calcule.
 *
 * Le mensuel de référence arrive **déjà arrondi** de l'API : appliquer le taux
 * dessus donne exactement ce qui sera enregistré, et l'écran ne peut pas
 * annoncer un montant que l'enregistrement démentirait.
 */
function montantDuTaux(pourcentage: number, mensuel: number | null): number | null {
  if (mensuel === null || !Number.isFinite(pourcentage)) return null;
  return Math.round(mensuel * pourcentage) / 100;
}

/**
 * Taux qu'il faut poser pour atteindre un montant.
 *
 * Arrondi à l'**entier** : c'est ce que la grille d'un contrat enregistre, et
 * un barème réglé à 64,25 % y deviendrait 64 % sans prévenir. L'écran affiche
 * ensuite le montant réellement produit par ce taux entier, qui peut donc
 * différer de quelques euros de celui qu'on a saisi — mieux vaut le voir que
 * de le découvrir sur un contrat.
 */
function tauxDuMontant(montant: number, mensuel: number | null): number | null {
  if (mensuel === null || mensuel <= 0 || !Number.isFinite(montant)) return null;
  return Math.round((montant / mensuel) * 100);
}

/**
 * Montant tel qu'il se saisit : virgule décimale, sans séparateur de milliers.
 *
 * Le séparateur de milliers casserait la frappe — il faudrait le retirer à
 * chaque caractère — et le point décimal se tape mal sur un clavier français.
 */
function montantSaisissable(montant: number | null): string | null {
  if (montant === null) return null;
  return montant.toFixed(2).replace('.', ',');
}

/** Nombre saisi, ou `null` quand la case est vide — « quelle que soit sa valeur ». */
function nombreOuNull(valeur: string): number | null {
  const texte = valeur.trim();
  if (texte === '') return null;
  const nombre = Number(texte);
  return Number.isFinite(nombre) ? nombre : null;
}


/** Nom lu des colonnes de bornes du barème, pour les champs de chaque ligne. */
const LIBELLES_BORNE = { ageMin: 'âge minimum', ageMax: 'âge maximum', rangAnnee: 'année de contrat' } as const;

export function ParametresRemuneration(): JSX.Element {
  const { data, isLoading, error } = useParametresRemuneration();
  const enregistrerValeur = useEnregistrerValeurReference();
  const enregistrerBareme = useEnregistrerBareme();
  const notifier = useToast();

  const [historiqueOuvert, setHistoriqueOuvert] = useState(false);
  const [baremeEdite, setBaremeEdite] = useState<string | null>(null);
  const [lignes, setLignes] = useState<LigneBrouillon[]>([]);
  const [dateEffetBareme, setDateEffetBareme] = useState(jourFormulaire());
  /**
   * Montant en cours de frappe, par ligne.
   *
   * Sans lui, le champ afficherait le montant **dérivé du taux** à chaque
   * caractère : taper « 1 » poserait un taux de 0 %, qui réécrirait aussitôt le
   * champ à « 0 ». On garde donc le texte saisi le temps de la frappe, et on
   * rend la main au montant calculé dès que le champ est quitté — c'est là que
   * l'écart entre le montant voulu et celui du taux entier se voit.
   */
  const [montantsSaisis, setMontantsSaisis] = useState<Record<number, string>>({});
  const [erreur, setErreur] = useState<string | null>(null);

  const [nouvelleValeur, setNouvelleValeur] = useState<string>('');
  const [dateEffetValeur, setDateEffetValeur] = useState(jourFormulaire());
  const [sourceValeur, setSourceValeur] = useState('');
  const [saisieValeurOuverte, setSaisieValeurOuverte] = useState(false);

  const horaire = useMemo(
    () => data?.valeurs.find((valeur) => valeur.code === 'SMIC_HORAIRE' && valeur.enVigueur) ?? null,
    [data],
  );
  const aVenir = useMemo(
    () => data?.valeurs.filter((valeur) => valeur.aVenir) ?? [],
    [data],
  );

  /**
   * Une valeur de référence qui n'a pas bougé depuis plus d'un an mérite d'être
   * vérifiée : le SMIC est revalorisé au moins chaque 1er janvier, et rien
   * d'autre ne signalerait que les montants affichés ont vieilli.
   */
  const valeurVieillie = useMemo(() => {
    if (!horaire) return false;
    const anciennete = Date.now() - new Date(horaire.dateEffet).getTime();
    return anciennete > 365 * 24 * 3600 * 1000;
  }, [horaire]);

  if (error) {
    return (
      <MessageErreur
        message={
          error instanceof ErreurApi
            ? error.message
            : 'Les paramètres de rémunération n’ont pas pu être chargés.'
        }
      />
    );
  }
  if (isLoading || !data) {
    return (
      <div className="rounded-2xl bg-white p-6 text-sm text-codialis-gray shadow-card">
        Lecture des paramètres de rémunération…
      </div>
    );
  }

  const ouvrirEdition = (bareme: BaremeRemuneration): void => {
    setErreur(null);
    setBaremeEdite(bareme.id);
    setLignes(enBrouillon(bareme));
    setMontantsSaisis({});
    // Un barème déjà utilisé ne se corrige pas : on propose d'emblée une
    // nouvelle date d'effet, celle du jour, plutôt que de laisser l'API refuser.
    setDateEffetBareme(
      bareme.contratsCalcules > 0 ? jourFormulaire() : jourFormulaire(bareme.dateEffet),
    );
  };

  /**
   * Modifie une case de la ligne.
   *
   * `montant` n'est pas une colonne : c'est l'autre façon de dire le même taux.
   * Le saisir revient à poser le pourcentage correspondant, et rien n'est
   * enregistré en euros — sans quoi le montant et le taux finiraient par se
   * contredire à la première revalorisation du SMIC.
   */
  const modifierLigne = (
    index: number,
    champ: keyof LigneBrouillon | 'montant',
    valeur: string,
  ): void => {
    setLignes((precedentes) =>
      precedentes.map((ligne, rang) => {
        if (rang !== index) return ligne;
        if (champ === 'libelleTranche') return { ...ligne, libelleTranche: valeur };
        if (champ === 'critere') return { ...ligne, critere: valeur.trim() === '' ? null : valeur };
        if (champ === 'base') return { ...ligne, base: valeur as LigneBrouillon['base'] };
        if (champ === 'pourcentage') return { ...ligne, pourcentage: nombreOuNull(valeur) ?? 0 };
        if (champ === 'montant') {
          const montant = Number(valeur.replace(/\s/g, '').replace(',', '.'));
          const taux = tauxDuMontant(montant, data.mensuelDeReference);
          return taux === null ? ligne : { ...ligne, pourcentage: taux };
        }
        return { ...ligne, [champ]: nombreOuNull(valeur) };
      }),
    );
  };

  const enregistrerLeBareme = async (bareme: BaremeRemuneration): Promise<void> => {
    setErreur(null);
    try {
      await enregistrerBareme.mutateAsync({
        code: bareme.code,
        libelle: bareme.libelle,
        dateEffet: dateEffetBareme,
        referenceCode: bareme.referenceCode,
        note: bareme.note,
        lignes: lignes.map((ligne, rang) => ({ ...ligne, ordre: rang + 1 })),
      });
      setBaremeEdite(null);
      notifier('Barème enregistré. Les prochains calculs l’utilisent.');
    } catch (e) {
      setErreur(
        e instanceof ErreurApi ? e.message : 'Le barème n’a pas pu être enregistré.',
      );
    }
  };

  const enregistrerLaValeur = async (): Promise<void> => {
    setErreur(null);
    const valeur = Number(nouvelleValeur.replace(',', '.'));
    if (!Number.isFinite(valeur) || valeur <= 0) {
      setErreur('La valeur attendue est un nombre positif, par exemple 12,31.');
      return;
    }
    try {
      await enregistrerValeur.mutateAsync({
        code: 'SMIC_HORAIRE',
        valeur,
        dateEffet: dateEffetValeur,
        ...(sourceValeur.trim() ? { source: sourceValeur.trim() } : {}),
      });
      setSaisieValeurOuverte(false);
      setNouvelleValeur('');
      setSourceValeur('');
      notifier('Valeur enregistrée. Les montants suivent sa date d’effet.');
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'La valeur n’a pas pu être enregistrée.');
    }
  };

  const champ =
    'min-h-[44px] w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-codialis-navy outline-none transition focus:border-codialis-sky focus:ring-4 focus:ring-sky-100';

  return (
    <section className="space-y-4">
      {erreur ? <MessageErreur message={erreur} /> : null}

      {/* ── Valeur de référence ─────────────────────────────────────────── */}
      <div className="rounded-2xl bg-white shadow-card">
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 px-4 py-4 sm:px-6">
          <div className="min-w-0">
            <h3 className="font-display text-lg font-bold text-codialis-navy">
              Valeur de référence
            </h3>
            <p className="mt-0.5 text-sm text-codialis-gray">
              Les montants de rémunération sont calculés à partir d’elle : aucun euro n’est
              saisi à côté d’un pourcentage.
            </p>
          </div>
          <BoutonSecondaire onClick={() => setSaisieValeurOuverte((ouverte) => !ouverte)}>
            <Plus className="mr-2 h-4 w-4" aria-hidden />
            Nouvelle valeur
          </BoutonSecondaire>
        </header>

        <div className="grid grid-cols-1 gap-4 px-4 py-4 sm:grid-cols-3 sm:px-6">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
              SMIC horaire
            </p>
            <p className="font-display text-2xl font-bold text-codialis-navy">
              {horaire ? `${horaire.valeur.toLocaleString('fr-FR')} ${horaire.unite}` : '—'}
            </p>
            {horaire ? (
              <p className="mt-0.5 text-xs text-codialis-gray">
                en vigueur depuis le {jourReunion(horaire.dateEffet)}
              </p>
            ) : null}
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
              Mensuel de référence
            </p>
            <p className="font-display text-2xl font-bold text-codialis-navy">
              {montantEuros(data.mensuelDeReference)}
            </p>
            <p className="mt-0.5 text-xs text-codialis-gray">
              calculé, jamais saisi : horaire × durée mensuelle
            </p>
          </div>
          <div className="flex items-start">
            <button
              type="button"
              onClick={() => setHistoriqueOuvert((ouvert) => !ouvert)}
              className="inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold text-codialis-sky hover:underline"
            >
              <History className="h-4 w-4" aria-hidden />
              {historiqueOuvert ? 'Masquer l’historique' : `Historique (${data.valeurs.length})`}
            </button>
          </div>
        </div>

        {valeurVieillie ? (
          <p className="mx-4 mb-4 flex gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 sm:mx-6">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            Cette valeur n’a pas changé depuis plus d’un an. Le SMIC est revalorisé au moins
            chaque 1er janvier : vérifiez-la avant de monter de nouveaux contrats.
          </p>
        ) : null}

        {aVenir.length > 0 ? (
          <p className="mx-4 mb-4 rounded-xl border border-sky-100 bg-sky-50 px-4 py-3 text-sm text-sky-950 sm:mx-6">
            {aVenir.length} valeur(s) saisie(s) à l’avance : elles s’appliqueront d’elles-mêmes
            à leur date d’effet, sans intervention.
          </p>
        ) : null}

        {saisieValeurOuverte ? (
          <div className="grid grid-cols-1 gap-3 border-t border-gray-100 px-4 py-4 sm:grid-cols-4 sm:px-6">
            <label className="min-w-0 text-xs font-medium text-codialis-gray">
              SMIC horaire brut
              <input
                type="text"
                inputMode="decimal"
                value={nouvelleValeur}
                onChange={(evenement) => setNouvelleValeur(evenement.target.value)}
                placeholder="12,31"
                className={`mt-1 ${champ}`}
              />
            </label>
            <label className="min-w-0 text-xs font-medium text-codialis-gray">
              Date d’effet
              <input
                type="date"
                value={dateEffetValeur}
                onChange={(evenement) => setDateEffetValeur(evenement.target.value)}
                className={`mt-1 ${champ}`}
              />
            </label>
            <label className="min-w-0 text-xs font-medium text-codialis-gray sm:col-span-2">
              Source
              <input
                type="text"
                value={sourceValeur}
                onChange={(evenement) => setSourceValeur(evenement.target.value)}
                placeholder="Décret, publication, note interne…"
                className={`mt-1 ${champ}`}
              />
            </label>
            <div className="sm:col-span-4">
              <BoutonMint
                onClick={() => void enregistrerLaValeur()}
                disabled={enregistrerValeur.isPending}
              >
                {enregistrerValeur.isPending ? 'Enregistrement…' : 'Enregistrer la valeur'}
              </BoutonMint>
            </div>
          </div>
        ) : null}

        {historiqueOuvert ? (
          <ul className="divide-y divide-gray-100 border-t border-gray-100">
            {data.valeurs.map((valeur) => (
              <li
                key={valeur.id}
                className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-3 sm:px-6"
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-codialis-navy">
                    {valeur.libelle} · {valeur.valeur.toLocaleString('fr-FR')} {valeur.unite}
                  </p>
                  <p className="text-xs text-codialis-gray">
                    à partir du {jourReunion(valeur.dateEffet)}
                    {valeur.source ? ` · ${valeur.source}` : ''}
                    {valeur.saisiPar ? ` · saisie par ${valeur.saisiPar}` : ''}
                  </p>
                </div>
                {valeur.enVigueur ? <Pastille texte="En vigueur" ton="success" /> : null}
                {valeur.aVenir ? <Pastille texte="À venir" ton="info" /> : null}
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {/* ── Barèmes ─────────────────────────────────────────────────────── */}
      {data.baremes
        .filter((bareme) => bareme.enVigueur || bareme.aVenir)
        .map((bareme) => {
          const edite = baremeEdite === bareme.id;
          const fige = bareme.contratsCalcules > 0;
          // Le critère n'a de sens que pour un barème qui s'en sert : l'afficher
          // partout imposerait une colonne vide sur l'apprentissage, où le taux
          // ne dépend que de l'âge et de l'année.
          const avecCritere = (edite ? lignes : bareme.lignes).some(
            (ligne) => ligne.critere !== null,
          );
          return (
            <div key={bareme.id} className="rounded-2xl bg-white shadow-card">
              <header className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 px-4 py-4 sm:px-6">
                <div className="min-w-0">
                  <h3 className="font-display text-lg font-bold text-codialis-navy">
                    Barème · {bareme.libelle}
                  </h3>
                  <p className="mt-0.5 text-sm text-codialis-gray">
                    En vigueur depuis le {jourReunion(bareme.dateEffet)}
                    {fige
                      ? ` · ${bareme.contratsCalcules} période(s) de contrat calculée(s) avec lui`
                      : ' · aucun contrat ne s’y appuie encore'}
                  </p>
                </div>
                {edite ? null : (
                  <BoutonSecondaire onClick={() => ouvrirEdition(bareme)}>
                    Régler le barème
                  </BoutonSecondaire>
                )}
              </header>

              {fige && edite ? (
                <p className="mx-4 mt-4 flex gap-2 rounded-xl border border-sky-100 bg-sky-50 px-4 py-3 text-sm text-sky-950 sm:mx-6">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  Ce barème a déjà servi à des contrats : il ne se corrige plus. L’enregistrement
                  crée un barème daté du jour choisi ; les contrats existants gardent le leur.
                </p>
              ) : null}

              <div className="overflow-x-auto px-4 py-4 sm:px-6">
                <table className="w-full min-w-[720px] text-left text-sm">
                  <thead>
                    <tr className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
                      <th className="pb-2 pr-3">Tranche</th>
                      <th className="pb-2 pr-3">Âge min</th>
                      <th className="pb-2 pr-3">Âge max</th>
                      <th className="pb-2 pr-3">Année</th>
                      {/* Le critère ne sert qu'aux barèmes réglés sur autre chose que
                          l'année — le diplôme préparé, en professionnalisation. Sur
                          l'apprentissage, une colonne toujours vide n'apprend rien. */}
                      {avecCritere ? <th className="pb-2 pr-3">Critère</th> : null}
                      <th className="pb-2 pr-3">%</th>
                      <th className="pb-2 pr-3">Base</th>
                      <th className="pb-2">Montant € / mois</th>
                      {edite ? (
                        <th className="pb-2">
                          <span className="sr-only">Actions</span>
                        </th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(edite ? lignes : bareme.lignes).map((ligne, index) => (
                      <tr key={edite ? index : (ligne as LigneBareme).id} className="align-middle">
                        <td className="py-2 pr-3">
                          {edite ? (
                            <input
                              type="text"
                              aria-label={`Ligne ${index + 1} · tranche`}
                              value={ligne.libelleTranche}
                              onChange={(evenement) =>
                                modifierLigne(index, 'libelleTranche', evenement.target.value)
                              }
                              className={champ}
                            />
                          ) : (
                            <span className="font-semibold text-codialis-navy">
                              {ligne.libelleTranche}
                            </span>
                          )}
                        </td>
                        {(['ageMin', 'ageMax', 'rangAnnee'] as const).map((colonne) => (
                          <td key={colonne} className="py-2 pr-3">
                            {edite ? (
                              <input
                                type="text"
                                inputMode="numeric"
                                aria-label={`Ligne ${index + 1} · ${LIBELLES_BORNE[colonne]}`}
                                value={ligne[colonne] === null ? '' : String(ligne[colonne])}
                                onChange={(evenement) =>
                                  modifierLigne(index, colonne, evenement.target.value)
                                }
                                placeholder={colonne === 'rangAnnee' ? 'toutes' : 'sans borne'}
                                className={`${champ} w-24`}
                              />
                            ) : (
                              <span className="text-codialis-gray">
                                {ligne[colonne] !== null
                                  ? ligne[colonne]
                                  : colonne === 'rangAnnee'
                                    ? 'toutes'
                                    : '—'}
                              </span>
                            )}
                          </td>
                        ))}
                        {avecCritere ? (
                          <td className="py-2 pr-3">
                            {edite ? (
                              <input
                                type="text"
                                aria-label={`Ligne ${index + 1} · critère`}
                                value={ligne.critere ?? ''}
                                onChange={(evenement) =>
                                  modifierLigne(index, 'critere', evenement.target.value)
                                }
                                placeholder="—"
                                className={`${champ} w-40`}
                              />
                            ) : (
                              <span className="text-codialis-gray">{ligne.critere ?? '—'}</span>
                            )}
                          </td>
                        ) : null}
                        <td className="py-2 pr-3">
                          {edite ? (
                            <input
                              type="text"
                              inputMode="decimal"
                              aria-label={`Ligne ${index + 1} · pourcentage`}
                              value={String(ligne.pourcentage)}
                              onChange={(evenement) =>
                                modifierLigne(index, 'pourcentage', evenement.target.value)
                              }
                              className={`${champ} w-24`}
                            />
                          ) : (
                            <span className="font-bold text-codialis-navy">
                              {ligne.pourcentage} %
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          {edite ? (
                            <select
                              aria-label={`Ligne ${index + 1} · base`}
                              value={ligne.base}
                              onChange={(evenement) =>
                                modifierLigne(index, 'base', evenement.target.value)
                              }
                              className={`${champ} w-44`}
                            >
                              <option value="SMIC">du SMIC</option>
                              <option value="MINIMUM_CONVENTIONNEL">du minimum conventionnel</option>
                            </select>
                          ) : (
                            <span className="text-codialis-gray">
                              {ligne.base === 'SMIC' ? 'du SMIC' : 'du minimum conventionnel'}
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-codialis-gray">
                          {edite ? (
                            // Saisir le montant plutôt que le taux : un employeur
                            // raisonne en euros, et personne ne sait de tête ce que
                            // 1 200 € font en pourcentage du SMIC.
                            <input
                              type="text"
                              inputMode="decimal"
                              aria-label={`Ligne ${index + 1} · montant mensuel`}
                              value={
                                montantsSaisis[index] ??
                                (ligne.base === 'SMIC'
                                  ? (montantSaisissable(
                                      montantDuTaux(ligne.pourcentage, data.mensuelDeReference),
                                    ) ?? '')
                                  : '')
                              }
                              onChange={(evenement) => {
                                const saisi = evenement.target.value;
                                setMontantsSaisis((precedents) => ({
                                  ...precedents,
                                  [index]: saisi,
                                }));
                                modifierLigne(index, 'montant', saisi);
                              }}
                              onBlur={() =>
                                setMontantsSaisis((precedents) => {
                                  const { [index]: _oublie, ...reste } = precedents;
                                  return reste;
                                })
                              }
                              disabled={ligne.base !== 'SMIC' || data.mensuelDeReference === null}
                              placeholder={ligne.base === 'SMIC' ? '1 200' : 'non calculable'}
                              className={`${champ} w-32 disabled:cursor-not-allowed disabled:bg-gray-50`}
                            />
                          ) : (
                            montantEuros((ligne as LigneBareme).montant)
                          )}
                        </td>
                        {edite ? (
                          <td className="py-2 text-right">
                            <button
                              type="button"
                              onClick={() =>
                                setLignes((precedentes) =>
                                  precedentes.filter((_, rang) => rang !== index),
                                )
                              }
                              aria-label={`Retirer la ligne ${ligne.libelleTranche}`}
                              className="grid min-h-[44px] min-w-[44px] place-items-center rounded-xl border border-gray-200 text-codialis-gray transition hover:border-red-200 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" aria-hidden />
                            </button>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {bareme.note ? (
                <p className="px-4 pb-4 text-xs text-codialis-gray sm:px-6">{bareme.note}</p>
              ) : null}

              {edite ? (
                <div className="flex flex-wrap items-end justify-between gap-3 border-t border-gray-100 px-4 py-4 sm:px-6">
                  <div className="flex flex-wrap items-end gap-3">
                    <BoutonSecondaire
                      onClick={() =>
                        setLignes((precedentes) => [
                          ...precedentes,
                          {
                            libelleTranche: 'Nouvelle tranche',
                            ageMin: null,
                            ageMax: null,
                            rangAnnee: null,
                            critere: null,
                            pourcentage: 0,
                            base: 'SMIC',
                            ordre: precedentes.length + 1,
                          },
                        ])
                      }
                    >
                      <Plus className="mr-2 h-4 w-4" aria-hidden />
                      Ajouter une règle
                    </BoutonSecondaire>
                    <label className="text-xs font-medium text-codialis-gray">
                      Applicable à partir du
                      <input
                        type="date"
                        value={dateEffetBareme}
                        onChange={(evenement) => setDateEffetBareme(evenement.target.value)}
                        className={`mt-1 ${champ} w-48`}
                      />
                    </label>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <BoutonSecondaire onClick={() => setBaremeEdite(null)}>Annuler</BoutonSecondaire>
                    <BoutonMint
                      onClick={() => void enregistrerLeBareme(bareme)}
                      disabled={enregistrerBareme.isPending}
                    >
                      <Save className="mr-2 h-4 w-4" aria-hidden />
                      {enregistrerBareme.isPending ? 'Enregistrement…' : 'Enregistrer le barème'}
                    </BoutonMint>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}

      <p className="text-xs text-codialis-gray">
        Une règle sans âge ni année s’applique à tout le monde, et la règle la plus précise
        l’emporte : c’est ainsi qu’une exception s’ajoute sans toucher au reste du barème.
        Le montant se saisit aussi bien que le taux — entrer 1 200 € pose le pourcentage du
        SMIC le plus proche, et affiche ce qu’il donne réellement.
      </p>
    </section>
  );
}
